import React, { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Flex, Text, VStack, Button, useToast } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useAppDispatch,
  setStep1,
  useLazyListPlateQuery,
  setCurrentStep,
  useAppSelector,
} from '@spartalabs/pdc-core';
import { Card, SimpleInput } from 'components';
import { DelayAnimation } from 'shared/DelayAnimation';
import * as z from 'zod';
import { NewScheduleHeader } from '../NewScheduleHeader';

export const VehicleForm = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    step1: { currentKm, fleetId, personId, plate },
  } = useAppSelector(state => state.scheduleSlicer);

  const [fetchPlate, { data: dataKm, isFetching, isSuccess }] =
    useLazyListPlateQuery();

  const VehicleFormData = z.object({
    plate: z.string().min(7, 'A placa deve ter 7 caracteres'),
    currentKm: z
      .string()
      .nonempty('Km não pode ser vazio')
      .refine(
        value => dataKm?.currentKm && Number(value) > dataKm?.currentKm,
        'O km informado é inválido, ele deve ser maior que o atual',
      )
      .refine(
        value => dataKm?.currentKm && dataKm?.currentKm + 25000 > Number(value),
        `O km informado não pode ser superior ao anterior (${dataKm?.currentKm}) em 25.000 km`,
      ),
    fleetId: z.string(),
    personId: z.string(),
  });

  type VehicleFormDataType = z.infer<typeof VehicleFormData>;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<VehicleFormDataType>({
    resolver: zodResolver(VehicleFormData),
  });

  const onSubmit = (data: VehicleFormDataType) => {
    if (!isSuccess) {
      toast({
        title: 'Algo deu errado.',
        position: 'top',
        description: 'Verificar placa digitada.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } else {
      dispatch(setStep1(data));
      navigate('/novo-agendamento/servicos');

      dispatch(setCurrentStep(2));
    }
  };

  const handleFetchPlate = useCallback(async () => {
    try {
      const fetchedData = await fetchPlate({
        plate: watch('plate'),
      }).unwrap();

      setValue('fleetId', fetchedData.fleetId);
      setValue('personId', fetchedData.personId);
    } catch (error) {
      toast({
        title: 'Algo deu errado.',
        position: 'top',
        description: 'Verificar placa digitada.',
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  }, [watch('plate')]);

  useEffect(() => {
    if (watch('plate').length === 7) {
      handleFetchPlate();
    }
  }, [watch('plate')]);

  useEffect(() => {
    if (currentKm) {
      setValue('currentKm', currentKm);
    }

    if (plate) {
      setValue('plate', plate);
    }

    if (fleetId) {
      setValue('fleetId', fleetId);
    }

    if (personId) {
      setValue('personId', personId);
    }
  }, [currentKm, fleetId, personId, plate]);

  return (
    <DelayAnimation>
      <NewScheduleHeader />

      <VStack
        w="100%"
        display="flex"
        align="center"
        justify="center"
        pt="44px"
        pb="44px"
      >
        <Flex flexDir="column" align="center" justify="center" mb="32px">
          <Text fontSize="18px" color="text.100">
            Dados sobre o veículo para agendamento
          </Text>
          <Text fontSize="14px" color="text.300" mt="3px">
            Insira as informações abaixo
          </Text>
        </Flex>

        <Card maxW="400px" w="100%" as="form">
          <Flex flexDir="column" gap="24px" mt="32px">
            <SimpleInput
              label="*Placa do Veículo"
              labelColor="text.300"
              {...register('plate')}
              defaultValue={plate || ''}
              maxLength={7}
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              size="lg"
              placeholder="Digite a placa do veículo"
              error={errors.plate?.message}
            />

            <SimpleInput
              isLoading={isFetching}
              label="*Km do Veículo"
              labelColor="text.300"
              type="text"
              {...register('currentKm')}
              id="currentKm"
              size="lg"
              bg="transparent"
              borderColor="gray.100"
              color="text.100"
              fontSize="16px"
              fontWeight="500"
              isDisabled={!dataKm?.currentKm || isFetching}
              placeholder={dataKm?.currentKm.toString()}
              defaultValue={currentKm || ''}
              error={errors.currentKm?.message}
            />
          </Flex>
          <Flex mt="32px" justify="end" gap="36px">
            <Button
              type="button"
              rounded="4px"
              bg="button"
              h="44px"
              onClick={handleSubmit(onSubmit)}
            >
              <Text>Continuar</Text>
            </Button>
          </Flex>
        </Card>
      </VStack>
    </DelayAnimation>
  );
};
